import React from 'react';

import {
	LoadingContainer,
	Wrapper,
	Header,
	HeaderRow,
	InputWrapper,
	Label,
	IconWrapper,
	FooterWrapper,
	TooltipContent,
	StyledPopover,
	NoData,
} from './ConfigureInitiative.styles';
import { ResultTable } from '../ResultTable';
import { Tooltip } from 'react-tippy';
import { ExpandableInput } from 'components/ExpandableInput';
import {
	ApLoaderDefault,
	ApLoaderText,
	ApToggleSwitch,
	ApInput,
	ApSelect,
	ApIcon,
	ApDatePicker,
	colors,
} from '@alixpartners/ui-components';
import moment from 'moment';
import { getStartOfWeek, ITEMS_TYPE } from '../../utils';

const WAVE_OPTIONS = [{ label: 'None', value: '' }];
const DAYS = 5;

export class ConfigureInitiative extends React.Component {
	state = {
		showCompanySpend: false,
		isWeekPickerOpen: false,
	};

	inputRef = React.createRef();

	componentDidMount() {
		this.setDefaults();
	}

	componentDidUpdate(prevProps) {
		const { taxonomies, data } = this.props;

		if (
			(taxonomies && taxonomies !== prevProps.taxonomies) ||
			(!prevProps.data && data)
		) {
			this.setDefaults();
		}
	}

	setDefaults = () => {
		const { taxonomies, initiativeData, onInitiativeChange, data } = this.props;
		const {
			items,
			initiativeItemType,
			tailSegmentsConfiguration,
			id,
		} = initiativeData;

		if (
			taxonomies &&
			(initiativeItemType === ITEMS_TYPE.CATEGORY ||
				initiativeItemType === ITEMS_TYPE.VENDOR)
		) {
			const itemsWithDefaults = items.map(item => {
				const defaultTaxonomy = taxonomies.find(taxonomy => {
					return (
						taxonomy.project_L1Category === item.hierarchy[0] &&
						taxonomy.project_L2Category === item.hierarchy[1] &&
						taxonomy.project_L3Category === item.hierarchy[2] &&
						taxonomy.project_L4Category === item.hierarchy[3]
					);
				});
				const defaults = {
					addressablePercentage: defaultTaxonomy
						? defaultTaxonomy.sourceablePercentage
						: 0,
					lowPercentage: defaultTaxonomy ? defaultTaxonomy.lowPercentage : 0,
					highPercentage: defaultTaxonomy ? defaultTaxonomy.highPercentage : 0,
					opexPercentage: 100,
					capexPercentage: 0,
				};

				return {
					...defaults,
					...item,
				};
			});

			onInitiativeChange({ items: itemsWithDefaults });
		}

		if (taxonomies && initiativeItemType === ITEMS_TYPE.TAIL_SPEND) {
			const itemsWithDefaults = items.map(item => {
				const defaultTaxonomy = taxonomies.find(taxonomy => {
					return (
						taxonomy.project_L1Category === item.hierarchy[0] &&
						taxonomy.project_L2Category === item.hierarchy[1] &&
						taxonomy.project_L3Category === item.hierarchy[2] &&
						taxonomy.project_L4Category === item.hierarchy[3]
					);
				});
				const defaultSegment = {
					lowPercentage: defaultTaxonomy ? defaultTaxonomy.lowPercentage : 0,
					highPercentage: defaultTaxonomy ? defaultTaxonomy.highPercentage : 0,
				};
				const defaults = {
					addressablePercentage: defaultTaxonomy
						? defaultTaxonomy.sourceablePercentage
						: 100,
					lowPercentage: defaultTaxonomy ? defaultTaxonomy.lowPercentage : 0,
					highPercentage: defaultTaxonomy ? defaultTaxonomy.highPercentage : 0,
					opexPercentage: 100,
					capexPercentage: 0,
					tailSegmentsLowHigh: tailSegmentsConfiguration.segments.map(
						segment => ({
							...defaultSegment,
							...segment,
						})
					),
				};
				let segmentsData = {};

				if (!item.segments && id && data) {
					const fullItem = data.find(dataItem => dataItem.name === item.name);

					if (fullItem) {
						segmentsData = {
							segments: fullItem.segments,
							segmentsByCompanies: fullItem.segmentsByCompanies,
						};
					}
				}

				return {
					...defaults,
					...segmentsData,
					...item,
				};
			});

			onInitiativeChange({ items: itemsWithDefaults });
		}
	};

	toggleCompanySpend = () => {
		const { showCompanySpend } = this.state;

		this.setState({ showCompanySpend: !showCompanySpend });
	};

	getWaveOptions = () => {
		const { waves } = this.props;

		return [
			...WAVE_OPTIONS,
			...waves.map(({ name, id }) => ({ label: name, value: id })),
		];
	};

	handleNameChange = name => {
		const { onInitiativeChange } = this.props;

		onInitiativeChange({ name });
	};

	handleDescriptionChange = ({ target }) => {
		const description = target.value;
		const { onInitiativeChange } = this.props;

		onInitiativeChange({ description });
	};

	handleWaveChange = value => {
		const { onInitiativeChange } = this.props;

		onInitiativeChange({ waveId: value ? Number(value) : undefined });
	};

	handleItemsChange = items => {
		const { onInitiativeChange } = this.props;

		onInitiativeChange({ items });
	};

	handleDurationChange = value => {
		const { onInitiativeChange } = this.props;
		let number = Number(value);

		if (number < 0) {
			number = 0;
		}

		onInitiativeChange({ duration: number });
	};

	toggleWeekPicker = () => {
		const { isWeekPickerOpen } = this.state;

		this.setState({ isWeekPickerOpen: !isWeekPickerOpen });
	};

	getFormattedStartWeek = () => {
		const { initiativeData } = this.props;
		const { executionStartDate } = initiativeData;

		if (!executionStartDate) {
			return '';
		}

		return `w/c ${moment(executionStartDate).format('D MMM YYYY')}`;
	};

	handleStartWeekChange = ([date]) => {
		const { onInitiativeChange } = this.props;
		const executionStartDate = getStartOfWeek(date);

		onInitiativeChange({ executionStartDate });
	};

	getSelectedDays = () => {
		const { executionStartDate } = this.props.initiativeData;

		if (!executionStartDate) {
			return [];
		}

		const selectedDays = [...Array(DAYS).keys()].map(index => {
			return moment(executionStartDate)
				.add(index, 'days')
				.toDate();
		});

		return selectedDays;
	};

	render() {
		const { showCompanySpend, isWeekPickerOpen } = this.state;
		const {
			companies,
			spendFormatter,
			currencySymbol,
			taxonomies,
			initiativeData,
			waves,
			data,
			onWaccChange,
			showTopVendors,
		} = this.props;
		const {
			duration,
			waveId,
			items,
			name,
			initiativeItemType,
			executionStartDate,
			wacc,
			tailSegmentsConfiguration,
			id,
			description,
		} = initiativeData;
		const segments =
			tailSegmentsConfiguration && tailSegmentsConfiguration.segments;

		if (
			!taxonomies ||
			!waves ||
			(initiativeItemType === ITEMS_TYPE.TAIL_SPEND && id && !data)
		) {
			return (
				<LoadingContainer>
					<ApLoaderDefault loaderText={() => <ApLoaderText />} />
				</LoadingContainer>
			);
		}

		return (
			<Wrapper>
				<Header>
					<HeaderRow>
						<InputWrapper>
							<ApInput
								onChange={this.handleNameChange}
								value={name}
								label="Initiative name"
							/>
						</InputWrapper>
						{(initiativeItemType === ITEMS_TYPE.PT_HARMONIZATION ||
							initiativeItemType === ITEMS_TYPE.PT_IMPROVEMENT) && (
							<InputWrapper width={80}>
								<ApInput
									symbol="%"
									value={String(wacc)}
									onChange={onWaccChange}
									label="WACC"
									type="number"
								/>
							</InputWrapper>
						)}

						<InputWrapper width={170} innerRef={this.inputRef}>
							<ApInput
								onClick={this.toggleWeekPicker}
								label="Start week"
								name="executionStartDate"
								value={this.getFormattedStartWeek()}
								readOnly
							/>
						</InputWrapper>
						{isWeekPickerOpen && (
							<StyledPopover
								referenceElement={this.inputRef.current}
								hideArrow
								onClose={this.toggleWeekPicker}
								modifiers={{
									offset: { offset: '0, 10' },
								}}
							>
								<ApDatePicker
									selectedDays={this.getSelectedDays()}
									srcRange={{
										from: moment(executionStartDate)
											.subtract(3, 'years')
											.toDate(),
										to: moment()
											.add(3, 'years')
											.toDate(),
									}}
									range={DAYS}
									onChange={this.handleStartWeekChange}
								/>
							</StyledPopover>
						)}
						<InputWrapper width={80}>
							<ApInput
								onChange={this.handleDurationChange}
								value={String(duration)}
								label="Duration"
								name="duration"
								type="number"
								symbol="w"
							/>
						</InputWrapper>
						<InputWrapper width={105}>
							<Label>Wave</Label>
							<ApSelect
								options={this.getWaveOptions()}
								onChange={this.handleWaveChange}
								value={waveId || ''}
							/>
						</InputWrapper>
						<IconWrapper>
							<Tooltip
								position="top"
								animation="fade"
								arrow
								html={
									<TooltipContent>
										Wave selection is not mandatory in initial initiative
										creation, and can be done on later stages.
									</TooltipContent>
								}
								trigger="mouseenter"
							>
								<ApIcon
									iconName="baseline_help"
									iconSize={16}
									iconColor={colors.grey6}
								/>
							</Tooltip>
						</IconWrapper>
					</HeaderRow>
					<HeaderRow>
						<InputWrapper>
							<Label>Initiative description</Label>
							<ExpandableInput
								maxLength={512}
								onChange={this.handleDescriptionChange}
								value={description || ''}
							/>
						</InputWrapper>
					</HeaderRow>
				</Header>
				{items && items.length > 0 ? (
					<ResultTable
						wacc={wacc}
						data={items}
						fullData={data}
						itemsType={initiativeItemType}
						companies={companies}
						spendFormatter={spendFormatter}
						showCompanySpend={showCompanySpend}
						onItemsChange={this.handleItemsChange}
						currencySymbol={currencySymbol}
						segments={segments}
						showTopVendors={showTopVendors}
					/>
				) : (
					<NoData>No data available</NoData>
				)}

				<FooterWrapper>
					<ApToggleSwitch
						checked={showCompanySpend}
						onChange={this.toggleCompanySpend}
					>
						Company spend
					</ApToggleSwitch>
				</FooterWrapper>
			</Wrapper>
		);
	}
}

export default ConfigureInitiative;
